import { debounce } from 'lodash';

import { LOG_TO_SCALYR } from 'common/constants';
import { addToLogQueue } from 'common/ScalyrLogger';

type CommitEventHandler = (name: string) => PerformanceMark | PerformanceMeasure;

type Options = { debounce?: number };

let enabled = false;
export const enablePerformanceLogger = () => {
  enabled = true;
};

const parameterStore: Record<string, { params: object; counter: number }> = {};

const bounces: Record<string, { counter: number; fn?: Function }> = {};

const buildParamId = (entry: PerformanceMark | PerformanceMeasure) =>
  `${entry.entryType}-${entry.name}-${entry.startTime}`;

/**
 * This function allows to construct a wrapper for `setMark` and `setMeasure` performance functions, with
 * support for debouncing the calls. It also keep tracks of the events, so later on, those not explicitly
 * sent, can be ignored.
 */
const setMarkOrMeasure = (commitEvent: CommitEventHandler) => (name: string, params_?: object, opts?: Options) => {
  if (!enabled) {
    return;
  }

  const { debounce: debounceTimeout } = opts || {};

  const params = typeof params_ === 'object' ? params_ : {};
  const bId = `${name}-${JSON.stringify(params)}`;
  bounces[bId] = bounces[bId] || {};
  bounces[bId].counter = (bounces[bId].counter || 0) + 1;

  function event() {
    const entry = commitEvent(name);

    const paramId = buildParamId(entry);

    parameterStore[paramId] = { params, counter: bounces[bId].counter };
    delete bounces[bId];
  }

  if (debounceTimeout) {
    (bounces[bId].fn = bounces[bId].fn || debounce(event, debounceTimeout))();
  } else {
    event();
  }
};

export const setMark = (name: string, params_?: object, opts?: Options) =>
  setMarkOrMeasure(
    (name) =>
      (window.performance.mark(name) as unknown as PerformanceMark) ||
      window.performance.getEntriesByType('mark').pop(),
  )(name, params_, opts);

export const setMeasure = (
  name: string,
  // TODO: Fix `startOrMeasureOptions` type so it matches with window.performance.measure
  startOrMeasureOptions?: string,
  endMark?: string | undefined,
  params_?: object,
  opts?: Options,
) =>
  setMarkOrMeasure(
    (name) =>
      (window.performance.measure(name, startOrMeasureOptions, endMark) as unknown as PerformanceMeasure) ||
      window.performance.getEntriesByType('measure').pop(),
  )(name, params_, opts);

// TODO: Once we enable the performanceLogger lab more widely, we'll put this
// in enablePerformanceLogger
export const initPerformanceObserver = () => {
  if (window.performance) {
    let entries = window.performance.getEntries().map((entry) => entry.toJSON());

    const observer = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        entries.push(entry.toJSON());
      });

      entries.forEach((entry) => {
        // This is to avoid an infinite loop between sending the PerformanceMark to Scalyr and
        // generating a new PerformanceMark
        if (entry.entryType === 'resource' && entry.name.includes(`opname=${LOG_TO_SCALYR}`)) {
          return;
        }

        if (entry.entryType === 'mark' || entry.entryType === 'measure') {
          const paramId = buildParamId(entry);
          // React generates insane amounts of marks and measures so for now we'll be ignoring ones that were
          // not created by our wrapper function
          if (!parameterStore[paramId]) {
            return;
          }
          const { params, counter } = parameterStore[paramId] || {};
          delete parameterStore[paramId];
          entry = { ...entry, params, counter };
        }

        addToLogQueue({ ...entry, _type: 'PerformanceEntry' });
      });

      entries = [];
    });

    observer.observe({ entryTypes: ['navigation', 'resource', 'paint', 'mark', 'measure'] });
  } else {
    throw new Error();
  }
};
